import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Gavel, Clock, MapPin, Building2, Zap, Wrench, ArrowRight,
  Loader2, TrendingDown, AlertCircle, FileText, ChevronLeft, Crown, Send,
  Check, X, Trophy, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppHeader } from "@/components/AppHeader";

const eur = (v) => v == null ? "—" : new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v));

function timeLeft(deadline) {
  if (!deadline) return { label: "—", urgency: "calm" };
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return { label: "abgelaufen", urgency: "expired" };
  const h = Math.floor(ms / 3.6e6);
  const m = Math.floor((ms % 3.6e6) / 6e4);
  if (h < 1) return { label: `${m} min`, urgency: "critical" };
  if (h < 24) return { label: `${h} h ${m} min`, urgency: "warn" };
  const d = Math.floor(h / 24);
  return { label: `${d} Tag${d === 1 ? "" : "e"}`, urgency: "calm" };
}

// =============================================================================
// LIST PAGE: /installer/auctions
// =============================================================================
export function InstallerAuctionsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/auctions/open");
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auktionen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  // Refresh every 30 s
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="installer-auctions-list">
      <AppHeader title="Offene Auktionen" back="/dashboard" />
      <main className="max-w-4xl mx-auto px-3 sm:px-5 py-5 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <Gavel className="text-[#00E575]" size={22} />
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold">Montage-Auktionen</h1>
            <p className="text-[12px] text-zinc-300 font-medium">Auto-Refresh alle 30 Sekunden · niedrigster Preis gewinnt</p>
          </div>
        </div>

        {loading && <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400 inline-flex w-full justify-center gap-2"><Loader2 size={16} className="animate-spin" /> lädt…</div>}

        {!loading && items.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">
            Aktuell keine offenen Auktionen. Schau später wieder rein.
          </div>
        )}

        {!loading && items.map((a) => {
          const tl = timeLeft(a.deadline);
          const ps = a.project_summary || {};
          return (
            <Link
              key={a.id}
              to={`/installer/auctions/${a.id}`}
              data-testid={`auction-card-${a.id}`}
              className="block bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 rounded-2xl p-4 transition-all"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-display text-lg font-bold">
                      WP-Montage {ps.wp_kw || ""} {ps.wp_brand && <span className="text-zinc-400 text-[13px]">· {ps.wp_brand}</span>}
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-zinc-400">
                    {(ps.zip || ps.city) && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {ps.zip} {ps.city}</span>}
                    {ps.heat_load_kw && <span className="inline-flex items-center gap-1"><Zap size={11} /> {ps.heat_load_kw} kW Heizlast</span>}
                    {ps.building_type && <span className="inline-flex items-center gap-1"><Building2 size={11} /> {ps.building_type}</span>}
                  </div>
                </div>
                <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] font-bold border ${tl.urgency === "critical" ? "bg-red-500/15 text-red-300 border-red-500/40" : tl.urgency === "warn" ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : tl.urgency === "expired" ? "bg-zinc-700/30 text-zinc-400 border-zinc-700" : "bg-[#00E575]/10 text-[#00E575] border-[#00E575]/30"}`}>
                  <Clock size={11} /> {tl.label}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                <Stat icon={TrendingDown} label="Aktuell bestes Gebot" value={a.current_best_bid_eur != null ? eur(a.current_best_bid_eur) : "Noch kein Gebot"} highlight={a.current_best_bid_eur != null} />
                <Stat icon={Send} label="Anzahl Gebote" value={String(a.current_bid_count || 0)} />
                <Stat icon={Crown} label="Deine Position" value={a.my_best_bid_eur ? "Du bietest" : "—"} />
              </div>
              <div className="text-[12px] text-zinc-300 inline-flex items-center gap-1">Details ansehen <ArrowRight size={12} /></div>
            </Link>
          );
        })}
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className="bg-[#09090B] border border-zinc-800 rounded-lg p-2.5">
      <div className="text-[9.5px] uppercase tracking-widest text-zinc-500 font-bold inline-flex items-center gap-1 mb-1"><Icon size={10} />{label}</div>
      <div className={`text-[13px] font-bold ${highlight ? "text-[#00E575]" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

// =============================================================================
// DETAIL PAGE: /installer/auctions/:id
// =============================================================================
export function InstallerAuctionDetail() {
  const { id } = useParams();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/auctions/${id}`);
      setAuction(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auktion konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const maxAllowed = useMemo(() => {
    if (!auction?.current_best_bid_eur) return null;
    return auction.current_best_bid_eur * (1 - (auction.min_bid_step_pct || 0.005));
  }, [auction]);

  const submitBid = async () => {
    const v = Number(bidAmount);
    if (!v || v <= 0) {
      toast.error("Bitte gültigen Betrag eingeben");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/auctions/${id}/bids`, { amount_eur: v });
      if (data.new_best) toast.success(`Du bist jetzt Bestbieter! ${eur(v)}`);
      else toast.info(`Gebot abgegeben (${eur(v)}). Aktuell bestes Gebot: ${eur(data.best_now_eur)}.`);
      setBidAmount("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gebot fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400"><Loader2 className="animate-spin mr-2" size={16} /> lädt…</div>;
  }
  if (!auction) return null;

  const tl = timeLeft(auction.deadline);
  const ps = auction.project_summary || {};
  const closed = auction.status !== "open";
  const isWinning = auction.my_position === 1;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="installer-auction-detail">
      <AppHeader title="Auktion-Detail" />
      <main className="max-w-3xl mx-auto px-3 sm:px-5 py-5 space-y-5">
        <Link to="/installer/auctions" className="inline-flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-200 font-medium">
          <ChevronLeft size={14} /> zurück zur Liste
        </Link>

        {/* Header / Live status */}
        <section className={`rounded-2xl p-5 border ${tl.urgency === "critical" ? "bg-red-500/5 border-red-500/40" : "bg-[#18181B] border-zinc-800"}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <div className="inline-flex items-center gap-2">
                <Gavel className="text-[#00E575]" size={20} />
                <h1 className="font-display text-xl font-bold">Montage-Auktion</h1>
                {closed && <span className="px-2 py-0.5 rounded-md bg-zinc-700/40 text-zinc-300 text-[12px] uppercase font-bold tracking-wider">geschlossen</span>}
              </div>
              <p className="text-[12px] text-zinc-400 mt-1 font-medium">{ps.wp_brand} {ps.wp_kw} · {ps.zip} {ps.city}</p>
            </div>
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold border ${tl.urgency === "critical" ? "bg-red-500/15 text-red-300 border-red-500/40 animate-pulse" : tl.urgency === "warn" ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : tl.urgency === "expired" ? "bg-zinc-700/30 text-zinc-400 border-zinc-700" : "bg-[#00E575]/10 text-[#00E575] border-[#00E575]/30"}`}>
              <Clock size={13} /> {tl.label}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <Stat icon={TrendingDown} label="Aktuell bestes Gebot" value={auction.current_best_bid_eur != null ? eur(auction.current_best_bid_eur) : "noch keins"} highlight={auction.current_best_bid_eur != null} />
            <Stat icon={Send} label="Gebote insgesamt" value={String(auction.current_bid_count || 0)} />
            <Stat icon={Crown} label="Deine Position" value={auction.my_position ? `Platz ${auction.my_position}${isWinning ? " 🏆" : ""}` : "noch nicht geboten"} highlight={isWinning} />
          </div>

          {auction.my_best_bid_eur && (
            <div className="mt-3 bg-[#09090B] border border-zinc-800 rounded-lg p-3 flex items-center justify-between">
              <div>
                <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">Dein bestes Gebot</div>
                <div className="text-[15px] font-bold text-zinc-100">{eur(auction.my_best_bid_eur)}</div>
              </div>
              {!isWinning && auction.current_best_bid_eur && (
                <div className="text-right">
                  <div className="text-[12px] uppercase tracking-widest text-amber-300 font-bold">überboten</div>
                  <div className="text-[12px] text-amber-200 font-medium">Differenz: {eur(auction.my_best_bid_eur - auction.current_best_bid_eur)}</div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Project briefing */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
          <h2 className="font-display text-base font-semibold mb-3 inline-flex items-center gap-2"><FileText size={16} className="text-[#00E575]" /> Projekt-Briefing</h2>
          <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
            <Field label="WP-Paket" value={ps.package_name || `${ps.wp_brand || ""} ${ps.wp_kw || ""}`.trim() || "—"} />
            <Field label="Heizlast" value={ps.heat_load_kw ? `${ps.heat_load_kw} kW` : "—"} />
            <Field label="Gebäudetyp" value={ps.building_type || "—"} />
            <Field label="Standort" value={`${ps.zip || ""} ${ps.city || ""}`.trim() || "—"} />
            <Field label="Wunsch-Montagezeitraum" value={auction.desired_montage_window || "flexibel"} />
            <Field label="Mindest-Bietschritt" value={`${(auction.min_bid_step_pct * 100).toFixed(1)} % unter Bestgebot`} />
          </dl>
          {auction.notes_for_installer && (
            <div className="mt-3 bg-[#09090B] border border-zinc-800 rounded-lg p-3">
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1">Hinweise vom Back-Office</div>
              <div className="text-[12.5px] text-zinc-200 whitespace-pre-line">{auction.notes_for_installer}</div>
            </div>
          )}
        </section>

        {/* Bid form */}
        {!closed && (
          <section className="bg-gradient-to-br from-[#00E575]/10 to-transparent border border-[#00E575]/30 rounded-2xl p-5" data-testid="auction-bid-form">
            <h2 className="font-display text-base font-semibold mb-3 inline-flex items-center gap-2"><Wrench size={16} className="text-[#00E575]" /> Dein Gebot abgeben</h2>
            <div className="bg-[#09090B] border border-zinc-700 rounded-xl p-3 mb-3">
              <label className="block text-[12px] uppercase tracking-widest text-zinc-400 mb-2 font-bold">Montagepreis brutto (€)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  data-testid="auction-bid-input"
                  placeholder={maxAllowed ? `z. B. ${maxAllowed.toFixed(2)}` : "z. B. 4500,00"}
                  className="flex-1 h-11 bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[14px] font-mono"
                />
                <button
                  onClick={submitBid}
                  disabled={submitting || !bidAmount}
                  data-testid="auction-bid-submit"
                  className="h-11 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Bieten
                </button>
              </div>
              {maxAllowed && (
                <p className="text-[10.5px] text-amber-300 mt-2 inline-flex items-center gap-1">
                  <AlertCircle size={11} /> Max. erlaubtes Gebot: <b>{eur(maxAllowed)}</b> ({(auction.min_bid_step_pct * 100).toFixed(1)} % unter aktuellem Bestgebot)
                </p>
              )}
            </div>
            <p className="text-[12px] text-zinc-400 leading-relaxed">
              <b>Wichtig:</b> Wer bei Auktionsende den niedrigsten Preis hat, gewinnt automatisch und hat danach <b>24 h</b>, den Auftrag aktiv zu bestätigen.
              Bei Ablehnung oder Schweigen fällt eine <b>Pönale von 5 %</b> des abgelehnten Gebots an.
            </p>
          </section>
        )}

        {closed && (
          <ClosedSection auction={auction} onAction={load} />
        )}
      </main>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</div>
      <div className="text-zinc-100 font-medium">{value}</div>
    </div>
  );
}

function ClosedSection({ auction, onAction }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const isWinner = user?.id && auction.winner_user_id === user.id;
  const responded = auction.winner_response;
  const deadlinePassed = auction.winner_response_deadline && new Date(auction.winner_response_deadline) <= new Date();
  const showActions = auction.status === "awaiting_decision" && isWinner && !responded && !deadlinePassed;
  const winningBid = auction.winning_bid_eur || auction.current_best_bid_eur;
  const penaltyEur = winningBid ? (winningBid * 0.05).toFixed(2) : null;

  const accept = async () => {
    if (!window.confirm(`Auftrag annehmen?\n\nGebot: ${eur(winningBid)}\n\nNach Bestätigung wirst du dem Back-Office gemeldet und kannst mit der Planung starten.`)) return;
    setBusy(true);
    try {
      await api.post(`/auctions/${auction.id}/award/accept`);
      toast.success("Auftrag angenommen — viel Erfolg!");
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annahme fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 5) {
      toast.error("Bitte gib einen kurzen Grund an (mind. 5 Zeichen).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/auctions/${auction.id}/award/reject`, { reason: rejectReason.trim() });
      toast.warning(`Auftrag abgelehnt. Pönale: ${eur(data.penalty_eur)}.`);
      setRejectOpen(false);
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ablehnung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // ----- Active winner gets full UI -----
  if (showActions) {
    return (
      <section className="bg-gradient-to-br from-[#00E575]/15 to-transparent border-2 border-[#00E575]/50 rounded-2xl p-5 space-y-4" data-testid="auction-winner-banner">
        <div className="flex items-center gap-2">
          <Trophy className="text-[#00E575]" size={26} />
          <div>
            <h2 className="font-display text-xl font-bold">Du hast gewonnen! 🏆</h2>
            <p className="text-[12px] text-zinc-300 font-medium">Bestätige den Auftrag innerhalb von 24 h, sonst fällt eine Pönale an.</p>
          </div>
        </div>
        <div className="bg-[#09090B] border border-zinc-700 rounded-xl p-3 grid grid-cols-2 gap-3">
          <Field label="Dein Gewinner-Gebot" value={<span className="text-[#00E575] font-bold text-[16px]">{eur(winningBid)}</span>} />
          <Field label="Frist zur Bestätigung" value={auction.winner_response_deadline ? new Date(auction.winner_response_deadline).toLocaleString("de-DE") : "—"} />
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-[12px] text-amber-200 flex items-start gap-2 font-medium">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <p>Bei Ablehnung oder Schweigen fällt eine <b>Pönale von 5 %</b> ({penaltyEur && eur(penaltyEur)}) auf dein abgelehntes Gebot an. Außerdem erhältst du einen <b>Strike</b> — nach 3 Strikes wird dein Account gesperrt.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={accept} disabled={busy} data-testid="auction-accept-btn" className="flex-1 h-11 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[14px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            Auftrag annehmen
          </button>
          <button onClick={() => setRejectOpen(true)} disabled={busy} data-testid="auction-reject-btn" className="h-11 px-4 rounded-lg bg-red-500/15 text-red-300 border border-red-500/40 font-bold text-[14px] inline-flex items-center justify-center gap-1.5 hover:bg-red-500/25 disabled:opacity-50">
            <X size={15} /> Ablehnen
          </button>
        </div>
        {rejectOpen && (
          <div className="bg-[#09090B] border border-red-500/40 rounded-xl p-3 space-y-3" data-testid="auction-reject-modal">
            <p className="text-[13px] text-zinc-200">Bitte gib einen Grund an — wird an das Back-Office gemeldet und die <b>5%-Pönale</b> ({penaltyEur && eur(penaltyEur)}) wird sofort berechnet.</p>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="z. B. Termin nicht mehr verfügbar, Kapazität ausgeschöpft, …" data-testid="auction-reject-reason" className="w-full bg-[#09090B] border border-zinc-700 rounded-lg p-2.5 text-zinc-50 focus:border-red-500/60 outline-none text-[13px]" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setRejectOpen(false)} className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium">Abbrechen</button>
              <button onClick={reject} disabled={busy || rejectReason.trim().length < 5} data-testid="auction-reject-confirm" className="h-9 px-4 rounded-lg bg-red-500/20 border border-red-500/50 text-red-200 font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-red-500/30 disabled:opacity-50">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />} Endgültig ablehnen
              </button>
            </div>
          </div>
        )}
      </section>
    );
  }

  // ----- All other closed states -----
  const winnerLabels = {
    accepted: { title: "Auftrag angenommen ✓", color: "[#00E575]", text: "Der Gewinner hat den Auftrag bestätigt. Back-Office übernimmt die weitere Koordination." },
    rejected: { title: "Auftrag wurde abgelehnt", color: "amber-400", text: "Back-Office prüft jetzt den Nächstbietenden." },
    timeout:  { title: "Auftrag verfallen (Frist)", color: "red-400", text: "Der Gewinner hat nicht innerhalb von 24 h geantwortet — der Auftrag fällt zurück." },
  };
  const meta = (responded && winnerLabels[responded]) || null;

  return (
    <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 text-center text-zinc-300">
      <Gavel className="mx-auto mb-2 text-zinc-500" size={28} />
      {meta ? (
        <>
          <p className={`font-bold mb-1 text-${meta.color}`}>{meta.title}</p>
          <p className="text-[12px] text-zinc-300 font-medium">{meta.text}</p>
        </>
      ) : (
        <>
          <p className="font-bold mb-1">Auktion ist beendet.</p>
          {auction.status === "awaiting_decision" && <p className="text-[12px] text-zinc-300 font-medium">Auf Gewinner-Entscheidung warten…</p>}
          {auction.status === "closed_no_bids" && <p className="text-[12px] text-zinc-300 font-medium">Keine Gebote eingegangen.</p>}
          {auction.status === "awarded" && <p className="text-[12px] text-zinc-300 font-medium">Vergeben — Back-Office übernimmt die weitere Abstimmung.</p>}
        </>
      )}
    </section>
  );
}
